import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowsClockwise, CheckCircle, WarningCircle, XCircle,
  Database, CreditCard, EnvelopeSimple, SimCard, Sparkle,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtDate } from "../../lib/format";

const SERVICE_META = {
  mongodb:      { label: "MongoDB",          icon: Database,        desc: "Database (orders · users · wallets · plans)" },
  neptunepay:   { label: "NeptunePay",       icon: CreditCard,      desc: "Payment gateway (checkout · refunds · recurring)" },
  resend:       { label: "Resend",           icon: EnvelopeSimple,  desc: "Transactional + DSAR emails" },
  yesim:        { label: "Yesim Partner API",icon: SimCard,         desc: "eSIM provisioning (live vs mock)" },
  emergent_llm: { label: "Emergent LLM",     icon: Sparkle,         desc: "AI-generated daily travel news" },
};

const STATUS_STYLE = {
  ok:    { dot: "bg-mint",       bar: "border-mint",        text: "text-mint",        Icon: CheckCircle },
  warn:  { dot: "bg-coral-deep", bar: "border-coral",       text: "text-coral-deep",  Icon: WarningCircle },
  error: { dot: "bg-destructive",bar: "border-destructive", text: "text-destructive", Icon: XCircle },
};

const OVERALL_COPY = {
  ok:    { title: "All services healthy",    sub: "Every integration is online and authenticated." },
  warn:  { title: "Some services need attention", sub: "Functionality is degraded but not broken. Check warnings below." },
  error: { title: "Critical service down",   sub: "One or more integrations are offline — payments or emails may be blocked." },
};

export default function AdminSettings() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/health");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-settings">
      <div className="overline mb-2">{t("admin.settings", "Settings")}</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest">
          {t("admin.connHealth", "Connection health")}
        </h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-ghost text-xs font-semibold flex items-center gap-1.5 text-cobalt-600 hover:bg-cobalt-50 disabled:opacity-50"
          data-testid="health-refresh"
        >
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          {loading ? t("common.checking", "Checking…") : t("common.refresh", "Refresh")}
        </button>
      </div>

      {data && (
        <>
          {/* Overall status banner */}
          <div
            className={`rounded-3xl border-2 ${STATUS_STYLE[data.overall].bar} p-6 mb-8 bg-white flex items-center gap-5`}
            data-testid={`health-overall-${data.overall}`}
          >
            {(() => { const Icon = STATUS_STYLE[data.overall].Icon; return <Icon size={36} weight="fill" className={STATUS_STYLE[data.overall].text} />; })()}
            <div className="flex-1">
              <div className={`font-display font-black text-xl tracking-tight ${STATUS_STYLE[data.overall].text}`}>
                {OVERALL_COPY[data.overall].title}
              </div>
              <div className="text-sm text-ink-500 mt-0.5">{OVERALL_COPY[data.overall].sub}</div>
            </div>
            <div className="text-xs text-ink-500 hidden md:block">{fmtDate(data.checked_at)}</div>
          </div>

          {/* Per-service grid */}
          <div className="grid sm:grid-cols-2 gap-5">
            {Object.entries(data.services).map(([key, svc]) => {
              const meta = SERVICE_META[key] || { label: key, icon: Database, desc: "" };
              const Icon = meta.icon;
              const style = STATUS_STYLE[svc.status] || STATUS_STYLE.warn;
              return (
                <div
                  key={key}
                  className="card-soft p-5 relative"
                  data-testid={`health-card-${key}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-cobalt-50 flex items-center justify-center text-cobalt-500 flex-shrink-0">
                      <Icon size={20} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} aria-hidden />
                        <h3 className="font-display font-bold text-base tracking-tight">{meta.label}</h3>
                      </div>
                      <p className="text-xs text-ink-500 mb-2">{meta.desc}</p>
                      <p className={`text-sm font-medium ${style.text} break-words`}>{svc.message}</p>
                      {svc.details && (
                        <p className="text-xs text-ink-500 mt-1 font-mono break-all">{svc.details}</p>
                      )}
                      {svc.hint && (
                        <div className="mt-3 text-xs bg-ink-50 rounded-xl p-3 text-ink-700 leading-relaxed">
                          <span className="font-semibold">{t("common.howToFix", "How to fix")}:</span> {svc.hint}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!data && loading && (
        <div className="card-soft p-12 text-center text-ink-500" data-testid="health-loading">
          <ArrowsClockwise size={28} weight="bold" className="animate-spin inline-block mb-3" />
          <div>{t("common.checking", "Checking…")}</div>
        </div>
      )}

    </div>
  );
}
